import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n.tsx';
import { clearPickers, pickerClosed, pickerOpened } from '../pickerGuard.ts';

interface Props {
  onPick: (file: File) => void;
  disabled?: boolean;
}

/**
 * Attachment picker.
 *
 * A bare <input type="file"> works, but on a phone it drops the user into a
 * generic file browser. Splitting it into typed inputs lets the OS offer the
 * right thing: `capture` opens the camera directly, and a narrow `accept`
 * makes the gallery show only photos.
 */
/**
 * Android's chooser reacts badly to a vague `accept`: a bare `audio/*` drops
 * the user into the Files app's "Recent" list, and an empty accept opens the
 * generic browser rather than anything useful. Listing concrete extensions
 * alongside the mime type is what makes it offer the right app.
 */
const SOURCES = [
  { key: 'camera', labelKey: 'attach.camera', icon: '📷', accept: 'image/*', capture: 'environment' },
  { key: 'image', labelKey: 'attach.image', icon: '🖼️', accept: 'image/png,image/jpeg,image/webp,image/gif,image/*' },
  { key: 'video', labelKey: 'attach.video', icon: '🎬', accept: 'video/mp4,video/quicktime,video/webm,video/*' },
  {
    key: 'audio',
    labelKey: 'attach.audio',
    icon: '🎵',
    accept: 'audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,.mp3,.m4a,.aac,.ogg,.wav,audio/*',
  },
  {
    key: 'file',
    labelKey: 'attach.document',
    icon: '📄',
    accept:
      'application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,application/*,text/*',
  },
] as const;

export function AttachMenu({ onPick, disabled }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const wrapRef = useRef<HTMLDivElement>(null);

  /**
   * Backstop for the picker flag.
   *
   * `cancel` on a file input is not supported everywhere, so a dismissed
   * chooser can leave the flag set and swallow the next real Back press. The
   * window regaining focus means the picker is gone either way.
   *
   * The delay matters: on returning from the camera the browser delivers focus,
   * the Back event and the input's `change` in an order it does not promise.
   * Clearing immediately would race the very event this guards against, so it
   * waits long enough for both to land. The only cost is a Back press within
   * that window being ignored, and the user has just come back to the app.
   */
  /**
   * `cancel` fires when a chooser is dismissed without picking anything. React
   * does not type it for file inputs, so it is attached to the elements
   * directly; the alternative is leaving the flag set on every cancelled pick
   * and waiting for the focus backstop below to clear it.
   */
  useEffect(() => {
    const elements = Object.values(inputs.current).filter(
      (el): el is HTMLInputElement => el !== null,
    );
    const onCancel = () => pickerClosed();
    elements.forEach((el) => el.addEventListener('cancel', onCancel));
    return () => elements.forEach((el) => el.removeEventListener('cancel', onCancel));
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onFocus = () => {
      clearTimeout(timer);
      timer = setTimeout(clearPickers, 1200);
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onAway = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onAway);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onAway);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="attach" ref={wrapRef}>
      {SOURCES.map((source) => (
        <input
          key={source.key}
          ref={(el) => {
            inputs.current[source.key] = el;
          }}
          type="file"
          hidden
          accept={source.accept}
          // Only meaningful on mobile; desktop browsers ignore it.
          capture={'capture' in source ? (source.capture as 'environment') : undefined}
          onChange={(event) => {
            pickerClosed();
            const file = event.target.files?.[0];
            if (file) onPick(file);
            // Reset so picking the same file twice still fires onChange.
            event.target.value = '';
            setOpen(false);
          }}
          // The `cancel` event is attached natively below: React's types do not
          // carry it for file inputs.
        />
      ))}

      <button
        type="button"
        className="icon"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-label={t('attach.label')}
        aria-expanded={open}
      >
        📎
      </button>

      {open && (
        <div className="attachMenu" role="menu">
          {SOURCES.map((source) => (
            <button
              key={source.key}
              type="button"
              role="menuitem"
              onClick={() => {
                pickerOpened();
                inputs.current[source.key]?.click();
              }}
            >
              <span aria-hidden="true">{source.icon}</span>
              {t(source.labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
